import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutlined as CheckCircleIcon,
  ErrorOutlineOutlined as ErrorIcon,
  HelpOutlineOutlined as UnknownIcon,
  RefreshOutlined as RefreshIcon,
} from '@mui/icons-material';
import apiClient from '../utils/axiosConfig';

type ServiceStatusValue = 'up' | 'down' | 'unknown';
type OverallStatusValue = 'healthy' | 'degraded' | 'down' | 'unknown';

interface ServiceStatus {
  name: string;
  status: ServiceStatusValue;
  latency_ms?: number | null;
  error?: string | null;
  url?: string | null;
  checked_at: string;
}

interface MonitoringStatusPayload {
  overall: OverallStatusValue;
  checked_at: string;
  services: ServiceStatus[];
}

// Poll every 30 seconds — tight enough to catch outages quickly but not so
// often that we hammer the downstream probes.
const POLL_INTERVAL_MS = 30_000;

const STATUS_COLORS: Record<ServiceStatusValue, string> = {
  up: '#2e7d32',
  down: '#c62828',
  unknown: '#9e9e9e',
};

const OVERALL_META: Record<
  OverallStatusValue,
  { label: string; color: string; severity: 'success' | 'warning' | 'error' | 'info' }
> = {
  healthy: { label: 'All systems operational', color: '#2e7d32', severity: 'success' },
  degraded: { label: 'Degraded — one or more services are down', color: '#ed6c02', severity: 'warning' },
  down: { label: 'Major outage — all services unreachable', color: '#c62828', severity: 'error' },
  unknown: { label: 'Unable to determine status', color: '#9e9e9e', severity: 'info' },
};

const formatTimestamp = (iso?: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString();
  } catch {
    return iso;
  }
};

const StatusIcon: React.FC<{ status: ServiceStatusValue }> = ({ status }) => {
  const sx = { fontSize: 28, color: STATUS_COLORS[status] };
  if (status === 'up') return <CheckCircleIcon sx={sx} />;
  if (status === 'down') return <ErrorIcon sx={sx} />;
  return <UnknownIcon sx={sx} />;
};

const MonitoringDashboard: React.FC = () => {
  const [data, setData] = useState<MonitoringStatusPayload | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchStatus = useCallback(async (isRefresh: boolean = false) => {
    if (isRefresh) {
      setRefreshing(true);
    }
    try {
      const response = await apiClient.get<MonitoringStatusPayload>('/api/monitoring/status');
      if (!isMounted.current) return;
      setData(response.data);
      setError(null);
    } catch (err: any) {
      if (!isMounted.current) return;
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to fetch monitoring status.';
      setError(typeof detail === 'string' ? detail : 'Failed to fetch monitoring status.');
    } finally {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(() => fetchStatus(false), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const overallMeta = useMemo(() => {
    if (!data) return OVERALL_META.unknown;
    return OVERALL_META[data.overall] ?? OVERALL_META.unknown;
  }, [data]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            System Monitoring
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Live health of AI Hub and all downstream services. Auto-refreshes every 30 seconds.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={() => fetchStatus(true)}
          disabled={refreshing || loading}
        >
          Refresh
        </Button>
      </Stack>

      {loading && !data && (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      )}

      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {data && (
        <>
          <Alert
            severity={overallMeta.severity}
            icon={<StatusIcon status={data.overall === 'healthy' ? 'up' : data.overall === 'unknown' ? 'unknown' : 'down'} />}
            sx={{
              mb: 3,
              '& .MuiAlert-message': { width: '100%' },
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {overallMeta.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Last checked: {formatTimestamp(data.checked_at)}
              </Typography>
            </Stack>
          </Alert>

          <Grid container spacing={2}>
            {data.services.map((service) => (
              <Grid item xs={12} sm={6} md={4} key={service.name}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    border: '1px solid #e0e0e0',
                    borderLeft: `4px solid ${STATUS_COLORS[service.status]}`,
                    borderRadius: 2,
                    height: '100%',
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" spacing={2}>
                    <StatusIcon status={service.status} />
                    <Box flex={1} minWidth={0}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                        {service.name}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                        <Chip
                          label={service.status.toUpperCase()}
                          size="small"
                          sx={{
                            backgroundColor: STATUS_COLORS[service.status],
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 20,
                          }}
                        />
                        {typeof service.latency_ms === 'number' && (
                          <Typography variant="caption" color="text.secondary">
                            {service.latency_ms} ms
                          </Typography>
                        )}
                      </Stack>
                      {service.error && (
                        <Tooltip title={service.error} placement="bottom-start">
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{
                              display: 'block',
                              mt: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {service.error}
                          </Typography>
                        </Tooltip>
                      )}
                      {service.url && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            mt: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {service.url}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Container>
  );
};

export default MonitoringDashboard;
